/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import classnames from 'classnames';
import * as React from 'react';

import { IImageDimension } from '../../../interfaces';
import { INativeImageProps, INativeImageState, NativeImage } from './native-image';

/**
 * Configuration for the thumbnail image.
 * All image configuration should be passes for the main original image. The component will compress the given image.
 */
export interface IThumbnailImageProps extends INativeImageProps {
    /**
     * The quality to use for the thumbnail.
     * If not specified, the default value will be used.
     *
     * Calculated empirically:
     * - lower values might result in loosing color information of the image;
     * - larger values will result in increasing image disk space size which will slow down thumbnail downloading.
     */
    thumbnailQuality?: number;

    /**
     * Specifies how much the size of the main image should decrease in order to get a thumbnail.
     * If not specified, the default value will be used.
     *
     * The real compressing factor might be different based on the minimum size dimension value.
     *
     * Calculated empirically:
     * - lower values might result in loosing details information of the image making the image unrecognizable comparing to the main image;
     * - larger values will result in increasing image disk space size which will slow down thumbnail downloading.
     */
    sizeCompressingFactor?: number;

    /**
     * Specifies minimum width/height in pixels to use for the thumbnail in case size compressing will produce too small image.
     * If not specified, the default value will be used.
     *
     * Calculated empirically:
     * - lower values might result in loosing details information of the image making the image unrecognizable comparing to the main image;
     * - larger values will result in increasing image disk space size which will slow down thumbnail downloading.
     */
    minimumSizeDimensionValue?: number;
}

/**
 * State of the thumbnail image.
 */
export interface IThumbnailImageState extends INativeImageState {}

/**
 * React component responsible for a thumbnail of the main image.
 * Thumbnail is a compressed low-resolution copy of main image with low quality.
 */
export class ThumbnailImage extends React.PureComponent<IThumbnailImageProps, INativeImageState> {
    /**
     * The default class for thumbnail image component.
     */
    private static _thumbnailClassName: string = 'msc-thumbnail_image';

    /**
     * The default quality to use for the thumbnail.
     * Calculated empirically:
     * - lower values might result in loosing color information of the image;
     * - larger values will result in increasing image disk space size which will slow down thumbnail downloading.
     */
    private static _defaultThumbnailQuality: number = 10;

    /**
     * Specifies how much the size of the main image should decrease in order to get a thumbnail.
     * Calculated empirically:
     * - lower values might result in loosing details information of the image making the image unrecognizable comparing to the main image;
     * - larger values will result in increasing image disk space size which will slow down thumbnail downloading.
     */
    private static _defaultSizeCompressingFactor: number = 0.25;

    /**
     * Specifies minimum width/height in pixels to use for the thumbnail in case size compressing will produce too small image.
     * Calculated empirically:
     * - lower values might result in loosing details information of the image making the image unrecognizable comparing to the main image;
     * - larger values will result in increasing image disk space size which will slow down thumbnail downloading.
     */
    private static _defaultMinimumSizeDimensionValue: number = 50;

    /**
     * Configuration for the thumbnail image.
     * The configuration includes overrides for the size and quality of the main image configuration which is stored in regular props.
     */
    private _thumbnailProps: IThumbnailImageProps;

    /**
     * Retrieves compressed image dimensions from the original image size.
     * @param {{ width: number; height: number }} size - The main image size dimensions.
     * @param {number} sizeCompressingFactor - Specifies how much the size of the main image should decrease in order to get a thumbnail.
     * @param {number} minimumSizeDimensionValue - Specifies minimum width/height in pixels to use for the thumbnail in case size compressing will produce too small image.
     * @returns {{ width: number; height: number }} - The new image size dimensions to use for the thumbnail.
     */
    private static _getCompressedSize(
        size: { width: number; height: number },
        sizeCompressingFactor: number,
        minimumSizeDimensionValue: number
    ): { width: number; height: number } {
        let width = size.width;
        let height = size.height;

        // Get the lowest dimension.
        const minSize = Math.min(width, height);
        // Calculate compressed size.
        const newSize = Math.floor(minSize * sizeCompressingFactor);
        // Get minimum compressed size in case calculated size was too small.
        const minNewSize = Math.max(newSize, minimumSizeDimensionValue);
        // In case the original image was smaller than the new calculated size, then use the original size without compressing.
        const defaultSize = Math.min(minNewSize, minSize);

        width = width || defaultSize;
        height = height || defaultSize;

        // If minimum of the size dimensions is bigger than the calculated compressed size, we need to update the dimensions to use compressed values.
        if (width <= height && width > defaultSize) {
            height = Math.floor((height * defaultSize) / width); // Calculate the larger dimension saving the image proportions.
            width = defaultSize; // Assign the compressed minimum dimension value as width is less than height.
        } else if (width > height && height > defaultSize) {
            width = Math.floor((width * defaultSize) / height);
            height = defaultSize;
        }

        return { width, height };
    }

    /**
     * Generates a copy of image dimension with the updated information about suppressing to use for the thumbnail.
     * @param {IImageDimension | undefined} imageDimension - The image dimension from the main image.
     * @param {number} thumbnailQuality - The default quality to use for the thumbnail.
     * @param {number} sizeCompressingFactor - Specifies how much the size of the main image should decrease in order to get a thumbnail.
     * @param {number} minimumSizeDimensionValue - Specifies minimum width/height in pixels to use for the thumbnail in case size compressing will produce too small image.
     * @returns {IImageDimension} - The new image dimension for the thumbnail.
     */
    private static _updateImageDimension(
        imageDimension: IImageDimension | undefined,
        thumbnailQuality: number,
        sizeCompressingFactor: number,
        minimumSizeDimensionValue: number
    ): IImageDimension {
        const newImageDimension = imageDimension ? { ...imageDimension } : { w: 0, h: 0 };

        // Query parameters for the image might include quality, width, height parameters. Those should be updated.
        newImageDimension.q = newImageDimension.q ?? '';
        const parameters = newImageDimension.q.split('&');
        const originalWidth = ThumbnailImage._extractParameterValue(parameters, 'w', newImageDimension.w);
        const originalHeight = ThumbnailImage._extractParameterValue(parameters, 'h', newImageDimension.h);
        const { width, height } = this._getCompressedSize(
            { width: originalWidth, height: originalHeight },
            sizeCompressingFactor,
            minimumSizeDimensionValue
        );

        newImageDimension.q = [
            ...parameters.filter(
                part => !part.startsWith('q=') && !part.startsWith('w=') && !part.startsWith('h=') && !part.startsWith('f=')
            ),
            `q=${thumbnailQuality}`,
            `w=${width}`,
            `h=${height}`,
            'f=jpg' // jpg provides better compression.
        ].join('&');
        newImageDimension.h = height;
        newImageDimension.w = width;

        return newImageDimension;
    }

    /**
     * Retrieves value from the list of parameters and converts it to a number.
     * @param {string[]} parameters List of parameters in format name=value.
     * @param {string} parameterName The name of parameter to look for.
     * @param {number} defaultValue The default value to use in case the parameter was not found in the list or is not a number.
     * @returns {number} The value for the paramter.
     */
    private static _extractParameterValue(parameters: string[], parameterName: string, defaultValue: number): number {
        const currentWidthParameter = parameters.find(part => part.startsWith(`${parameterName}=`)) || `${parameterName}=${defaultValue}`;
        try {
            return Number.parseInt(currentWidthParameter.split(`${parameterName}=`)[1], 10);
        } catch {
            return defaultValue;
        }
    }

    /**
     * Initializes component with the main image props and generates props for the thumbnail.
     * @param {IThumbnailImageProps} props - Configuration of the component.
     */
    public constructor(props: IThumbnailImageProps) {
        super(props);

        this._thumbnailProps = { ...props };

        if (props.imageSettings) {
            const thumbnailQuality = props.thumbnailQuality ?? ThumbnailImage._defaultThumbnailQuality;
            const sizeCompressingFactor = props.sizeCompressingFactor ?? ThumbnailImage._defaultSizeCompressingFactor;
            const minimumSizeDimensionValue = props.minimumSizeDimensionValue ?? ThumbnailImage._defaultMinimumSizeDimensionValue;

            this._thumbnailProps.imageSettings = { ...props.imageSettings };
            this._thumbnailProps.imageSettings.viewports = { ...props.imageSettings.viewports };

            this._thumbnailProps.imageSettings.quality = ThumbnailImage._defaultThumbnailQuality;
            this._thumbnailProps.imageSettings.viewports.lg = ThumbnailImage._updateImageDimension(
                this._thumbnailProps.imageSettings.viewports.lg,
                thumbnailQuality,
                sizeCompressingFactor,
                minimumSizeDimensionValue
            );
            this._thumbnailProps.imageSettings.viewports.md = ThumbnailImage._updateImageDimension(
                this._thumbnailProps.imageSettings.viewports.md,
                thumbnailQuality,
                sizeCompressingFactor,
                minimumSizeDimensionValue
            );
            this._thumbnailProps.imageSettings.viewports.sm = ThumbnailImage._updateImageDimension(
                this._thumbnailProps.imageSettings.viewports.sm,
                thumbnailQuality,
                sizeCompressingFactor,
                minimumSizeDimensionValue
            );
            this._thumbnailProps.imageSettings.viewports.xl = ThumbnailImage._updateImageDimension(
                this._thumbnailProps.imageSettings.viewports.xl,
                thumbnailQuality,
                sizeCompressingFactor,
                minimumSizeDimensionValue
            );
            this._thumbnailProps.imageSettings.viewports.xs = ThumbnailImage._updateImageDimension(
                this._thumbnailProps.imageSettings.viewports.xs,
                thumbnailQuality,
                sizeCompressingFactor,
                minimumSizeDimensionValue
            )!;
        }

        this._thumbnailProps.quality = ThumbnailImage._defaultThumbnailQuality;
    }

    /**
     * Renders the thumbnail.
     */
    public render(): React.ReactNode {
        return <NativeImage {...this._thumbnailProps} className={classnames(ThumbnailImage._thumbnailClassName, this.props.className)} />;
    }
}
